import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { ConfigentoState, ConfigentoStore } from './store';
import { createConfigentoStore } from './store';
import type { CharacteristicSelection } from './selections';
import type { ValidationError } from '../validation/validate';

const StoreContext = createContext<ConfigentoStore | null>(null);

interface StoreProviderProps {
    /** Optional pre-built store (useful for tests). If omitted, a fresh store is created per Provider instance. */
    store?: ConfigentoStore;
    children: ReactNode;
}

/**
 * Provides a per-instance {@link ConfigentoStore} to its descendants. Each
 * mounted Configento instance gets its own provider so two instances on the
 * same page have independent selection state.
 */
export function StoreProvider({ store, children }: StoreProviderProps) {
    // useMemo so a single store survives re-renders without leaking across instances.
    const instanceStore = useMemo(() => store ?? createConfigentoStore(), [store]);
    return <StoreContext.Provider value={instanceStore}>{children}</StoreContext.Provider>;
}

function useConfigentoStore(): ConfigentoStore {
    const store = useContext(StoreContext);
    if (!store) {
        throw new Error('useConfigento store hooks must be used inside <StoreProvider>');
    }
    return store;
}

/** Public alias of {@link useConfigentoStore} — same store, no internal renaming. */
export function useConfigentoStoreRef(): ConfigentoStore {
    return useConfigentoStore();
}

/** Read the (typed) Selection for a single Characteristic. */
export function useCharacteristicSelection(
    characteristicId: number,
): CharacteristicSelection | undefined {
    const store = useConfigentoStore();
    return useStore(store, (s) => s.selections[characteristicId]);
}

const EMPTY_ERRORS: ReadonlyArray<ValidationError> = Object.freeze([]);

/**
 * Read the surfaced validation errors for a single Characteristic. Returns
 * a stable empty array reference when there are none, so callers can use
 * `errors.length` without churning React renders.
 */
export function useCharacteristicValidationErrors(
    characteristicId: number,
): ReadonlyArray<ValidationError> {
    const store = useConfigentoStore();
    return useStore(store, (s) => s.validationErrors[characteristicId] ?? EMPTY_ERRORS);
}

/** Errors of several Characteristics in one list, in the given order. */
export function useValidationErrorsFor(characteristicIds: readonly number[]): ReadonlyArray<ValidationError> {
    const store = useConfigentoStore();
    return useStore(
        store,
        useShallow((s) => characteristicIds.flatMap((id) => s.validationErrors[id] ?? EMPTY_ERRORS)),
    );
}

/** Imperative setter for the full validation-error list. */
export function useSetValidationErrors(): ConfigentoState['setValidationErrors'] {
    const store = useConfigentoStore();
    return useStore(store, (s) => s.setValidationErrors);
}

/**
 * Read the imperative action set. The selector returns a fresh object on
 * every call, so we run it through {@link useShallow} — without shallow
 * comparison Zustand would notify on every store change (the new object
 * is reference-unequal) and trigger an infinite update loop in production
 * (Minified React error #185). `renderToStaticMarkup` doesn't reproduce
 * this — it runs a single SSR pass with no commit phase — so unit tests
 * silently pass while the deployed bundle crashes on mount.
 */
export function useStoreActions(): Pick<
    ConfigentoState,
    'selectValue' | 'toggleValue' | 'setText' | 'setQuantity' | 'setPickQty' | 'setFiles' | 'setCaptchaPayload' | 'resetSelection' | 'applyPredefine'
> {
    const store = useConfigentoStore();
    return useStore(store, useShallow(selectActions));
}

function selectActions(s: ConfigentoState) {
    return {
        selectValue: s.selectValue,
        toggleValue: s.toggleValue,
        setText: s.setText,
        setQuantity: s.setQuantity,
        setPickQty: s.setPickQty,
        setFiles: s.setFiles,
        setCaptchaPayload: s.setCaptchaPayload,
        resetSelection: s.resetSelection,
        applyPredefine: s.applyPredefine,
    };
}
